import { useMemo, useState } from 'react'
import { useLanguage } from '../hooks/useLanguage'

interface TokenSearchProps {
  symbols: string[]
  selectedToken?: string
  onSelect: (token?: string) => void
}

export function TokenSearch({ symbols, selectedToken, onSelect }: TokenSearchProps) {
  const { t } = useLanguage()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)

  const suggestions = useMemo(() => {
    const normalized = query.trim().toUpperCase()
    if (!normalized) return symbols.slice(0, 8)
    return symbols.filter((symbol) => symbol.includes(normalized)).slice(0, 8)
  }, [query, symbols])

  const select = (token: string) => {
    onSelect(token)
    setQuery('')
    setOpen(false)
  }

  return (
    <section className="border-b border-gray-200 bg-white px-4 py-2.5 dark:border-gray-800 dark:bg-gray-900">
      <div className="relative mx-auto max-w-3xl">
        <div className={`flex h-10 items-center gap-2 rounded-xl border bg-gray-50 px-3 transition-colors dark:bg-gray-800 ${
          open ? 'border-blue-400 ring-2 ring-blue-500/10' : 'border-gray-200 dark:border-gray-700'
        }`}>
          <span className="text-gray-400" aria-hidden="true">⌕</span>
          {selectedToken ? (
            <>
              <span className="rounded-md bg-blue-100 px-2 py-0.5 text-sm font-bold text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                {selectedToken}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-gray-500 dark:text-gray-400">
                {t('相关事件', 'related events')}
              </span>
              <button
                type="button"
                onClick={() => onSelect(undefined)}
                className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-white"
                aria-label={t('清除 Token 筛选', 'Clear token filter')}
              >
                ×
              </button>
            </>
          ) : (
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value.toUpperCase())
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => window.setTimeout(() => setOpen(false), 120)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && suggestions[0]) select(suggestions[0])
                if (event.key === 'Escape') setOpen(false)
              }}
              placeholder={t('搜索 Token，例如 BTC、ETH', 'Search token, e.g. BTC, ETH')}
              className="min-w-0 flex-1 bg-transparent text-sm font-medium uppercase text-gray-900 outline-none placeholder:normal-case placeholder:text-gray-400 dark:text-white"
            />
          )}
        </div>

        {open && !selectedToken && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1.5 rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900">
            <div className="mb-2 flex items-center gap-2 px-1">
              <span className="text-xs text-gray-400">{t('快速选择', 'Quick select')}</span>
              {['BTC', 'ETH'].map((token) => (
                <button
                  key={token}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => select(token)}
                  className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700 hover:bg-blue-100 hover:text-blue-700 dark:bg-gray-800 dark:text-gray-200"
                >
                  {token}
                </button>
              ))}
            </div>
            <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
              {suggestions.map((token) => (
                <button
                  key={token}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => select(token)}
                  className="rounded-md px-2.5 py-1.5 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-700 dark:text-gray-200 dark:hover:bg-blue-950/50 dark:hover:text-blue-300"
                >
                  {token}
                </button>
              ))}
              {suggestions.length === 0 && (
                <span className="px-2 py-1 text-sm text-gray-400">{t('没有匹配的 Token', 'No matching token')}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
